import { Button } from "@rio-cloud/rio-uikit";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { useState } from "react";
import { useT } from "@hooks";

interface InactiveItem {
  id: string;
  name: string;
  fieldName: string;
}

interface ReactivateDialogProps {
  show: boolean;
  item: InactiveItem | null;
  entityLabel: string;
  transactionLabel: string;
  onReactivate: (id: string, fieldName: string) => Promise<void>;
  onCancel: () => void;
}

const ReactivateDialog = ({
  show,
  item,
  entityLabel,
  transactionLabel,
  onReactivate,
  onCancel,
}: ReactivateDialogProps) => {
  const t = useT();
  const [step, setStep] = useState<"confirm" | "success">("confirm");
  const [loading, setLoading] = useState(false);

  if (!item) return null;

  const { id: itemId, fieldName: itemFieldName } = item;

  const handleReactivate = async () => {
    setLoading(true);
    try {
      await onReactivate(itemId, itemFieldName);
      setStep("success");
    } catch {
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("confirm");
    onCancel();
  };

  if (step === "success") {
    return (
      <Dialog
        show={show}
        title={
          <span className="text-medium text-size-16">{t("dialog.reactivate.title", { entity: entityLabel })}</span>
        }
        body={
          <div className="padding-20">
            <div className="display-flex flex-column align-items-center justify-content-center">
              <span className="rioglyph rioglyph-ok-sign text-size-h2 text-color-primary" aria-hidden="true"></span>
              <p className="text-medium text-size-16 margin-top-10">{t("dialog.reactivate.success.heading")}</p>
              <p className="text-color-gray text-center">
                {t("dialog.reactivate.success.body", {
                  entity: entityLabel,
                  name: item.name,
                  transaction: transactionLabel,
                })}
              </p>
            </div>
          </div>
        }
        footer={
          <div className="display-flex flex-wrap justify-content-end">
            <Button bsStyle={Button.PRIMARY} onClick={handleClose} className="rounded-small width-100pct-xs">
              {t("dialog.reactivate.goToForm")}
            </Button>
          </div>
        }
        onClose={handleClose}
        showCloseButton
        bsSize="md"
      />
    );
  }

  return (
    <Dialog
      show={show}
      title={<span className="text-medium text-size-16">{t("dialog.reactivate.title", { entity: entityLabel })}</span>}
      body={
        <div className="padding-20">
          <p className="text-color-gray">{t("dialog.reactivate.instruction", { entity: entityLabel })}</p>
          <div className="form-group">
            <input type="text" className="form-control text-color-danger" value={item.name} readOnly />
          </div>
        </div>
      }
      footer={
        <div className="display-flex flex-wrap gap-10 justify-content-end">
          <Button onClick={handleClose} className="width-150 rounded-small width-100pct-xs">
            {t("common.cancel")}
          </Button>
          <Button
            bsStyle={Button.PRIMARY}
            onClick={handleReactivate}
            disabled={loading}
            className="width-150 rounded-small width-100pct-xs"
          >
            {t("dialog.reactivate.button")}
          </Button>
        </div>
      }
      onClose={handleClose}
      showCloseButton
      bsSize="md"
    />
  );
};

export type { InactiveItem };
export default ReactivateDialog;
